// ============================================================
// FILE: src/components/ServicesOffered/NGO/NGOSocialMediaPage.tsx
// ADD ROUTE: your route for NGO page
// ============================================================

import ContactUsForm from "@/pages/ContactUsForm";
import {
  SectionLabel, SectionHeading, GoldButton, OutlineButton,
  BulletList, ProcessSteps, FAQAccordion, CTABand,
  EmbedPlaceholder, ClientStrip, GalleryGrid,
} from "../../ui/Components";
import { FONTS, GLOBAL_CSS } from "../../ui/styles";
import ServiceSection from "@/components/HomePage/ServicesSection";
import { FaInstagram, FaFacebook, FaExternalLinkAlt } from "react-icons/fa";


const processSteps = [
  { num: "01", title: "NGO Audit & Mission Mapping", desc: "We study your mission, beneficiary profile, existing donor base and social presence." },
  { num: "02", title: "Donor Persona & Content Strategy", desc: "We define your donor segments and map content themes to trust-building and donation intent." },
  { num: "03", title: "Campaign Calendar & Creative Production", desc: "Monthly calendar aligned to causes, festivals and fundraising windows. All creatives designed in-house." },
  { num: "04", title: "Publishing & Donor Engagement", desc: "Scheduled posts, story updates, DM handling and comment responses to nurture donor relationships." },
  { num: "05", title: "Impact Reports & Monthly Review", desc: "We track reach, engagement, click-throughs to donation pages and recurring donor growth monthly." },
];

const faqs = [
  { q: "Do you manage donation campaigns during emergencies or disasters?", a: "Yes. We have rapid-response workflows to launch emergency appeal campaigns within 24 48 hours with urgency messaging, impact numbers and clear CTAs." },
  { q: "Will you link directly to our own donation page?", a: "Always. We never push donors to third-party aggregators. All campaigns point to your own 80G-enabled donation page for full control over donor data." },
  { q: "Do you handle 80G and transparency communication?", a: "Yes. We design donor education posts that explain 80G benefits, publish impact updates and share utilization reports in a clean, trustworthy format." },
  { q: "Can you manage multiple NGO handles simultaneously?", a: "Yes, we manage multiple handles for NGOs that run separate programmes or regional branches under one unified content system." },
];

const galleryItems = [
  { label: "Donor Impact Story", tag: "NGO", emoji: "" },
  { label: "80G Explainer Post", tag: "Awareness", emoji: "" },
  { label: "Emergency Appeal Banner", tag: "Campaign", emoji: "" },
  { label: "Volunteer Feature Reel", tag: "Story", emoji: "" },
  { label: "Monthly Impact Carousel", tag: "Report", emoji: "" },
  { label: "Festival Cause Post", tag: "Festival", emoji: "" },
  { label: "Donation Drive Banner", tag: "Campaign", emoji: "" },
  { label: "Beneficiary Story Card", tag: "Impact", emoji: "" },
];

const whatWeDoCards = [
  {
    title: "Impact Storytelling", icon: "",
    bullets: [
      "Stories of beneficiaries, volunteers and ground-level impact",
      "Before/after narratives and transformation posts that build emotional trust",
      "Reels capturing field work, events and community impact",
    ],
  },
  {
    title: "Donation Drive Campaigns", icon: "",
    bullets: [
      "Campaigns synced with donation drives (festivals, emergencies, monthly causes)",
      "Urgency messaging with real-time counters and milestone posts",
      "Clear CTAs leading to your own donation pages, not third-party platforms",
    ],
  },
  {
    title: "Donor Education & Trust", icon: "",
    bullets: [
      "Donor education posts around 80G tax benefits, transparency and reports",
      "Annual report highlights in digestible carousel formats",
      "Q&A posts that answer common donor doubts upfront",
    ],
  },
  {
    title: "Community & Volunteer Growth", icon: "",
    bullets: [
      "Volunteer spotlight posts to humanize your team",
      "Community-building content: polls, challenges, awareness days",
      "Cause amplification through shareable graphics and awareness campaigns",
    ],
  },
];

const heroStats = [
  { val: "10+ Cr", label: "Donations Raised" },
  { val: "3X", label: "Avg Donor Growth" },
  { val: "6+", label: "NGOs Managed" },
  { val: "80G", label: "Compliant Comms" },
];

/* ─── Mahipath Sinh Foundation Showcase Items ─── */
const mahipathGridItems = [
  {
    id: 1,
    handle: "@mahipathsinh_foundation",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/MahipathSinh-Foundation.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/MahipathNgoSocialMedia15.png",
    title: "Mahipath Sinh Foundation Post 1",
    link: "https://www.instagram.com/popular/mahipat-singh-foundation/",
    tag: "Impact Feature",
    imgStyle: {
      imageRendering: "-webkit-optimize-contrast",
      imageRendering: "crisp-edges",
      transform: "translateZ(0)",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden"
    }
  },
  {
    id: 2,
    handle: "@mahipathsinh_foundation",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/MahipathSinh-Foundation.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/MahipathNgoSocialMedia12.png",
    title: "Mahipath Sinh Foundation Post 2",
    link: "https://www.instagram.com/popular/mahipat-singh-foundation/",
    tag: "Environment"
  },
  {
    id: 3,
    handle: "@mahipathsinh_foundation",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/MahipathSinh-Foundation.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/MahipathNgoSocialMedia13.png",
    title: "Mahipath Sinh Foundation Post 3",
    link: "https://www.instagram.com/popular/mahipat-singh-foundation/",
    tag: "Education"
  },
  {
    id: 4,
    handle: "@mahipathsinh_foundation",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/MahipathSinh-Foundation.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/MahipathNgoSocialMedia14.png",
    title: "Mahipath Sinh Foundation Post 4",
    link: "https://www.instagram.com/popular/mahipat-singh-foundation/",
    tag: "Health Drive"
  }
];

/* ─── Nityanand Ashram Showcase Items ─── */
const nityanandGridItems = [
  {
    id: 1,
    handle: "@nityanand_ashram",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Nitynand-Ashram.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/NityaSocialMedia1.png", // i will replace the images latter
    title: "Nityanand Ashram Post 1",
    link: "https://www.instagram.com/pdspm.nityanand/",
    tag: "Upliftment"
  },
  {
    id: 2,
    handle: "@nityanand_ashram",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Nitynand-Ashram.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/NityaSocialMedia2.png", // i will replace the images latter
    title: "Nityanand Ashram Post 2",
    link: "https://www.instagram.com/pdspm.nityanand/",
    tag: "Spiritual Service"
  },
  {
    id: 3,
    handle: "@nityanand_ashram",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Nitynand-Ashram.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/NityaSocialMedia3.png", // i will replace the images latter
    title: "Nityanand Ashram Post 3",
    link: "https://www.instagram.com/pdspm.nityanand/",
    tag: "Education Drive"
  },
  {
    id: 4,
    handle: "@nityanand_ashram",
    avatar: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Nitynand-Ashram.webp",
    imgUrl: "https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Social-Media-Pages-Images/NityaSocialMedia4.png", // i will replace the images latter
    title: "Nityanand Ashram Post 4",
    link: "https://www.instagram.com/pdspm.nityanand/",
    tag: "Community Care"
  }
];

export default function NGOSocialMediaPage() {
  return (
    <div style={{ background: "#090909", color: "#F5F0E8", minHeight: "100vh", fontFamily: "'EB Garamond', serif" }}>
      <style>{`
        ${FONTS}
        ${GLOBAL_CSS}
        .back-link {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Montserrat',sans-serif; font-size: 11px;
          letter-spacing: 0.1em; text-transform: uppercase;
          color: rgba(201,168,76,0.65); text-decoration: none;
          margin-bottom: 32px; transition: color 0.2s;
        }
        .back-link:hover { color: #C9A84C; }
        .what-card { background: #090909; padding: 40px 32px; }
        @media (max-width: 600px) { .what-card { padding: 28px 20px; } }

        /* ── HERO SECTION ─────────────────────────────────────── */
        .ngo-hero-section {
          min-height: 90vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding-top: 20px;
          padding-bottom: 40px;
          padding-left: clamp(16px, 5vw, 80px);
          padding-right: clamp(16px, 5vw, 80px);
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .ngo-hero-section {
            padding-top: 60px;
            padding-bottom: 80px;
          }
        }

        /* ── INLINE STATS GRID ────────────────────────────────── */
        .ngo-hero-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          width: 100%;
          max-width: 680px;
          margin-top: 48px;
          background: rgba(201,168,76,0.1);
        }
        @media (min-width: 640px) {
          .ngo-hero-stats {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        .ngo-stat-item {
          background: #090909;
          text-align: center;
          padding: 28px 16px;
          margin: 1px;
        }
        .ngo-stat-val {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(28px, 5vw, 36px);
          font-weight: 600;
          color: #C9A84C;
          line-height: 1;
        }
        .ngo-stat-label {
          font-family: 'Montserrat', sans-serif;
          font-size: 10px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(245,240,232,0.4);
          margin-top: 8px;
        }

        /* ── LIVE SOCIAL FEED ─────────────────────────────────── */
        .social-feed-grid {
          display: grid;
          grid-template-columns: repeat(1, 1fr);
          gap: 32px;
          margin-top: 40px;
          max-width: 1100px;
          margin-left: auto;
          margin-right: auto;
        }
        @media (min-width: 640px) {
          .social-feed-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        .social-feed-card {
          background: #0d0d0d;
          border: 1px solid rgba(201,168,76,0.15);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          border-radius: 8px;
        }
        .social-feed-card:hover {
          border-color: rgba(201,168,76,0.5);
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.4), 0 0 20px rgba(201,168,76,0.1);
        }
        .feed-header {
          padding: 16px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(255,255,255,0.02);
          border-bottom: 1px solid rgba(201,168,76,0.1);
          z-index: 2;
        }
        .feed-user {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .feed-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
          padding: 2px;
        }
        .feed-avatar img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 2px solid #0d0d0d;
          object-fit: cover;
        }
        .feed-handle {
          font-family: 'Montserrat', sans-serif;
          font-size: 13px;
          font-weight: 600;
          color: #F5F0E8;
        }
        .feed-content {
          flex: 1;
          background: rgba(201,168,76,0.02);
          overflow: hidden;
          position: relative;
          min-height: 0;
        }
        .feed-iframe {
          width: 100%;
          height: 100%;
          border: none;
          overflow: auto;
        }
        .feed-img {
          transition: all 0.4s ease;
        }
        .feed-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          opacity: 0;
          transition: opacity 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          z-index: 4;
        }
        .social-feed-card:hover .feed-overlay {
          opacity: 1;
        }
        .social-feed-card:hover .feed-img {
          transform: scale(1.06);
          opacity: 0.35;
        }
      `}</style>


      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="ngo-hero-section" style={{ paddingTop: 0 }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 70% 50% at 50% 40%, rgba(201,168,76,0.09) 0%, transparent 70%)", pointerEvents: "none", paddingTop: 30 }} />
        <div style={{ position: "absolute", inset: 0, backgroundImage: "repeating-linear-gradient(45deg,rgba(201,168,76,0.02) 0px,transparent 1px,transparent 60px)", pointerEvents: "none" }} />

        <div style={{
          maxWidth: 1280,
          marginTop: "-20px",
          width: "100%",
          position: "relative",
          zIndex: 1,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 100, paddingTop: 30 }}>
            <span style={{ fontSize: 52 }}></span>
            <SectionLabel>NGO · Non-Profit</SectionLabel>
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond',serif",
            fontSize: "clamp(40px,6.5vw,84px)", fontWeight: 300,
            lineHeight: 1.1, color: "#F5F0E8", marginBottom: 28, maxWidth: 860,
          }}>
            Social Media Marketing<br /><em style={{ color: "#C9A84C" }}>for NGOs</em>
          </h1>
          <p style={{
            fontSize: "clamp(17px,2vw,21px)", lineHeight: 1.75,
            color: "rgba(245,240,232,0.72)", maxWidth: 660, marginBottom: 20,
            fontFamily: "'EB Garamond',serif",
          }}>
            We turn your mission into consistent, donor-friendly storytelling. Every post, reel and campaign is designed to build trust, show impact and drive recurring donations through your own infrastructure.
          </p>
          <p style={{
            fontSize: 14, color: "rgba(201,168,76,0.85)",
            fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.03em",
            marginBottom: 44, paddingLeft: 16,
            borderLeft: "2px solid rgba(201,168,76,0.4)",
            textAlign: "left",
            maxWidth: 660,
          }}>
            10+ Cr in donations facilitated through structured social campaigns for NGOs like yours.
          </p>
          {/* <div style={{ display: "center", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
            <GoldButton>Start NGO Social Media</GoldButton>
            
          </div> */}

          {/* ✅ Inline stats grid 2×2 on mobile, 4-col on desktop */}
          <div className="ngo-hero-stats">
            {heroStats.map((s) => (
              <div key={s.label} className="ngo-stat-item">
                <div className="ngo-stat-val">{s.val}</div>
                <div className="ngo-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NGO SOCIAL SHOWCASE ── */}
      <section style={{ padding: "20px clamp(16px,5vw,80px)", background: "rgba(201,168,76,0.02)", borderTop: "1px solid rgba(201,168,76,0.08)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <SectionHeading
            label="Social Showcase"
            title={<>Social Media Feeds<br /><em style={{ color: "#C9A84C" }}>We Manage</em></>}
            subtitle="Curated stories of beneficiaries, field missions, and campaign impact."
          />

          {/* 1. Mahipath Sinh Foundation Showcase */}
          <div style={{ marginBottom: 80 }}>
            <h3 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(24px, 3.5vw, 32px)",
              color: "#C9A84C",
              marginBottom: 24,
              borderBottom: "1px solid rgba(201,168,76,0.15)",
              paddingBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 12
            }}>
              <img
                src="https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/MahipathSinh-Foundation.webp"
                alt="Mahipath Sinh Foundation Logo"
                style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "1px solid rgba(201,168,76,0.3)" }}
              />
              Mahipath Sinh Foundation
              <span style={{ fontSize: 13, fontFamily: "'Montserrat', sans-serif", color: "rgba(245,240,232,0.4)", fontWeight: 400, marginLeft: "auto" }}>
                @mahipathsinh_foundation
              </span>
            </h3>

            <div className="social-feed-grid">
              {mahipathGridItems.map((item) => (
                <div
                  key={item.id}
                  className="social-feed-card"
                  onClick={() => window.open(item.link, '_blank')}
                >
                  {/* Header */}
                  <div className="feed-header">
                    <div className="feed-user">
                      <div className="feed-avatar">
                        <img
                          src={item.avatar}
                          alt={`${item.handle} Avatar`}
                        />
                      </div>
                      <span className="feed-handle">{item.handle}</span>
                    </div>
                    <FaInstagram style={{ color: '#E4405F', fontSize: 18 }} />
                  </div>

                  {/* Image Container */}
                  <div className="feed-content" style={{ position: "relative", width: "100%", height: "auto", overflow: "hidden" }}>
                    <img
                      src={item.imgUrl}
                      alt={item.title}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        transition: "all 0.4s ease",
                        ...((item as any).imgStyle || {})
                      }}
                      className="feed-img"
                    />

                    {/* Hover Overlay */}
                    <div className="feed-overlay">
                      <FaInstagram style={{ color: "#C9A84C", fontSize: 44, display: "inline-block", zIndex: 5 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Nityanand Ashram Showcase */}
          <div>
            <h3 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(24px, 3.5vw, 32px)",
              color: "#C9A84C",
              marginBottom: 24,
              borderBottom: "1px solid rgba(201,168,76,0.15)",
              paddingBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 12
            }}>
              <img
                src="https://pub-8d8c06eb82144fca803dab6ccecd7b41.r2.dev/Images/Nitynand-Ashram.webp"
                alt="Nityanand Ashram Logo"
                style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "1px solid rgba(201,168,76,0.3)" }}
              />
              Nityanand Ashram
              <span style={{ fontSize: 13, fontFamily: "'Montserrat', sans-serif", color: "rgba(245,240,232,0.4)", fontWeight: 400, marginLeft: "auto" }}>
                @nityanand_ashram
              </span>
            </h3>

            <div className="social-feed-grid">
              {nityanandGridItems.map((item) => (
                <div
                  key={item.id}
                  className="social-feed-card"
                  onClick={() => window.open(item.link, '_blank')}
                >
                  {/* Header */}
                  <div className="feed-header">
                    <div className="feed-user">
                      <div className="feed-avatar">
                        <img
                          src={item.avatar}
                          alt={`${item.handle} Avatar`}
                        />
                      </div>
                      <span className="feed-handle">{item.handle}</span>
                    </div>
                    <FaInstagram style={{ color: '#E4405F', fontSize: 18 }} />
                  </div>

                  {/* Image Container */}
                  <div className="feed-content" style={{ position: "relative", width: "100%", height: "auto", overflow: "hidden" }}>
                    <img
                      src={item.imgUrl}
                      alt={item.title}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        transition: "all 0.4s ease"
                      }}
                      className="feed-img"
                    />

                    {/* Hover Overlay */}
                    <div className="feed-overlay">
                      <FaInstagram style={{ color: "#C9A84C", fontSize: 44, display: "inline-block", zIndex: 5 }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ── WHAT WE DO ── */}
      <section style={{ padding: "20px clamp(16px,5vw,80px)", borderTop: "1px solid rgba(201,168,76,0.1)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <SectionHeading
            label="Our NGO Approach"
            title={<>What We Do for<br /><em style={{ color: "#C9A84C" }}>Your NGO on Social</em></>}
            subtitle="Impact-led content systems that move hearts and open wallets built around your cause, not generic formats."
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 2, background: "rgba(201,168,76,0.07)" }}>
            {whatWeDoCards.map((card) => (
              <div key={card.title} className="what-card">
                <div style={{ fontSize: 32, marginBottom: 12 }}>{card.icon}</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 500, color: "#F5F0E8", marginBottom: 18 }}>{card.title}</div>
                <BulletList items={card.bullets} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ──────────────────────────────────────────── */}
      <section style={{ padding: "20px clamp(16px,5vw,80px)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <SectionHeading
            label="Our Process"
            title={<>How We Run Social<br /><em style={{ color: "#C9A84C" }}>for Your NGO</em></>}
          />
          <ProcessSteps steps={processSteps} />
        </div>
      </section>



      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section style={{ padding: "20px clamp(16px,5vw,80px)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <SectionHeading
            label="Quick Answers"
            title={<>NGO Social Media<br /><em style={{ color: "#C9A84C" }}>FAQ</em></>}
          />
          <FAQAccordion faqs={faqs} />
        </div>
      </section>

      <ContactUsForm />
      <ServiceSection />
    </div>
  );
}